type BrowserCacheOperation = 'access' | 'clear' | 'read' | 'parse' | 'write' | 'remove'

export class BrowserCache {
  public available: boolean = false
  private cache: Storage | null = null
  private clearingCache: boolean = false

  private keys = {
    shift: false,
    ctrl: false,
    x: false
  }

  constructor(_addListeners: boolean = false, storage?: Storage) {
    if (storage) {
      this.available = true
      this.cache = storage
      return
    }

    if (typeof window === 'undefined') {
      return
    }

    try {
      this.cache = window.localStorage
      this.available = true
    } catch (error) {
      this.reportFailure('access', undefined, error)
    }
  }

  async clear(): Promise<boolean> {
    if (!this.cache || !this.available) {
      return false
    }

    if (this.keys.shift && this.keys.ctrl && this.keys.x && !this.clearingCache) {
      this.clearingCache = true
      try {
        this.cache.clear()
        console.log('cache cleared')
        return true
      } catch (error) {
        this.reportFailure('clear', undefined, error)
        return false
      } finally {
        this.clearingCache = false
      }
    }

    return false
  }

  get<R>(key: string): R | null {
    if (!this.cache || !this.available) {
      return null
    }

    let cached: string | null
    try {
      cached = this.cache.getItem(key)
    } catch (error) {
      this.reportFailure('read', key, error)
      return null
    }

    if (cached == null) {
      return null
    }

    try {
      return JSON.parse(cached) as R
    } catch (error) {
      this.reportFailure('parse', key, error, cached.length)
      this.remove(key)
      return null
    }
  }

  add(key: string, value: string | object | number | boolean): boolean {
    if (!this.cache || !this.available) {
      return false
    }

    let serialized: string | undefined
    try {
      serialized = JSON.stringify(value)
    } catch (error) {
      this.reportFailure('write', key, error)
      return false
    }

    if (serialized === undefined) {
      this.reportFailure('write', key, new TypeError('Value is not JSON-serializable'))
      return false
    }

    try {
      this.cache.setItem(key, serialized)
      return true
    } catch (error) {
      this.reportFailure('write', key, error, serialized.length)
      return false
    }
  }

  remove(key: string): boolean {
    if (!this.cache || !this.available) {
      return false
    }

    try {
      this.cache.removeItem(key)
      return true
    } catch (error) {
      this.reportFailure('remove', key, error)
      return false
    }
  }

  private reportFailure(
    operation: BrowserCacheOperation,
    key: string | undefined,
    error: unknown,
    serializedLength?: number
  ): void {
    console.warn('Browser cache operation failed', {
      operation,
      ...(key ? { key } : {}),
      ...(serializedLength == null ? {} : { serializedLength }),
      errorName: error instanceof Error ? error.name : 'UnknownError'
    })
  }
}
